import type {
  GraphEntityType,
  GraphFrameListItem,
  GraphOpportunityActionRequest,
  GraphOpportunityFeedItem,
  GraphRelationshipReminder,
  GraphVisualizationFrame,
  SaleskoOAuthProvider
} from "@salesko/contracts";
import { Avatar, Badge, Button, ConfidenceMeter, EmptyState, ListRow, SegmentedControl, Sheet, Spinner } from "@salesko/design-system";
import {
  ArrowUp,
  Bell,
  BellPlus,
  Bot,
  CalendarDays,
  Check,
  CircleDollarSign,
  ExternalLink,
  Flame,
  GitBranch,
  Globe,
  Inbox as InboxIcon,
  Loader2,
  Plug,
  Search,
  Share2,
  Shield,
  Sparkles,
  TrendingUp,
  UserRound,
  WifiOff,
  X,
  XCircle,
  Save,
  type LucideIcon
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode
} from "react";
import { LanguageSwitcher, useI18n, type Dictionary } from "../../i18n";
import type { StoredSaleskoSession } from "../../lib/api";
import { AccountSurface } from "../components/AccountSurface";
import { ActivityFeedPanel, type ActivityFeedPanelProps } from "../components/ActivityFeedPanel";
import { entityMeta } from "../entityMeta";
import { entityLabel, entitySingularLabel } from "../i18n";
import { compactDateTime } from "../lib/format";
import {
  opportunityActionAlreadyApplied,
  opportunityActionLabel,
  opportunityFollowUpRequestedAt,
  opportunityHasEvidence,
  opportunityInboxCounts,
  opportunityInsightType,
  opportunityReason,
  opportunitySourceFrameRecordId,
  opportunityStatusLabel
} from "../lib/opportunityInbox";
import { stableGraphFrameSummaryTitle } from "../lib/stableText";
import { GraphFlat } from "./GraphFlat";
import type { AgentChatMessage, AgentChatMode } from "../types";

type GraphNode = GraphVisualizationFrame["graph"]["nodes"][number];

type MobileTab = "graph" | "signals" | "inbox" | "you";
type GraphView = "graph" | "list" | "timeline";
type InstallPromptState = { canPrompt: boolean; installed: boolean; prompt: () => void };
type SignalAction = GraphOpportunityActionRequest["action"];
type EntityTone = "person" | "company" | "event" | "opportunity" | "note";
type BadgeTone = EntityTone | "neutral";

export function openFrameInMobileGraph(
  frameRecordId: string,
  onOpenFrame: (frameRecordId: string, nodeId?: string) => void,
  onShowGraph: () => void,
  nodeId?: string
) {
  onOpenFrame(frameRecordId, nodeId);
  onShowGraph();
}

type MobileNotification = {
  id: string;
  body: string;
  icon: LucideIcon;
  onOpen: () => void;
  title: string;
  tone: BadgeTone;
  when: string;
};

export type MobileWorkspaceProps = {
  frame: GraphVisualizationFrame;
  filters: Record<GraphEntityType, boolean>;
  onToggleFilter: (type: GraphEntityType) => void;
  selectedNode: GraphNode | null;
  onSelectNode: (nodeId: string) => void;
  onCloseDetail: () => void;
  isSelfNode: boolean;
  onSetSelfNode: () => void;
  onClearSelfNode: () => void;
  message: string;
  agentMode: AgentChatMode;
  onAgentModeChange: (mode: AgentChatMode) => void;
  onMessageChange: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmitAgent: () => void;
  onUseSample: () => void;
  sampleText: string;
  chatMessages: AgentChatMessage[];
  agentBusy: boolean;
  signals: {
    activity: ActivityFeedPanelProps | null;
    actionError: string | undefined;
    actionPendingId: string | null;
    count: number;
    enabled: boolean;
    error: string | undefined;
    items: GraphOpportunityFeedItem[];
    onAction: (opportunityId: string, action: SignalAction) => void;
    onOpenFrame: (frameRecordId: string) => void;
    pending: boolean;
    readOnly: boolean;
  };
  inbox: {
    activeFrameRecordId: string | null;
    error: boolean;
    items: GraphFrameListItem[];
    onSelectFrameRecord: (frameRecordId: string) => void;
    pending: boolean;
    restoreErrorFrameId: string | null;
  };
  profile: {
    agentAuthLabel: string;
    availableAuthProviders: SaleskoOAuthProvider[];
    install: InstallPromptState | undefined;
    onOpenBilling: () => void;
    onOpenSettings: () => void;
    onSignIn: (provider: SaleskoOAuthProvider) => void;
    onSignOut: () => void;
    storedSession: StoredSaleskoSession | null;
  };
  notifications: {
    error: string | undefined;
    onOpenReminderFrame: (target: { frameRecordId: string; nodeId: string }) => void;
    onOpenSignalFrame: (frameRecordId: string) => void;
    opportunitySignals: GraphOpportunityFeedItem[];
    pending: boolean;
    relationshipReminders: GraphRelationshipReminder[];
  };
};

const FILTER_ORDER: GraphEntityType[] = ["person", "organisation", "event", "opportunity"];
const signalActions = [
  { action: "dismiss", icon: XCircle },
  { action: "save", icon: Save },
  { action: "follow_up", icon: BellPlus }
] satisfies Array<{ action: SignalAction; icon: LucideIcon }>;

function nodeSubtitle(node: GraphNode, t: Dictionary): string {
  if (node.summary && node.summary.trim().length > 0) {
    return node.summary;
  }
  return entitySingularLabel(t, node.type);
}

export function MobileWorkspace(props: MobileWorkspaceProps) {
  const { t } = useI18n();
  const { frame, filters, onToggleFilter, selectedNode, onSelectNode, onCloseDetail, isSelfNode, onSetSelfNode, onClearSelfNode } =
    props;
  const [tab, setTab] = useState<MobileTab>("graph");
  // Opening a saved frame updates the graph canvas; make that canvas visible on mobile.
  const handleOpenSignalFrame = useCallback(
    (frameRecordId: string) => {
      openFrameInMobileGraph(frameRecordId, props.signals.onOpenFrame, () => setTab("graph"));
    },
    [props.signals.onOpenFrame]
  );
  const handleOpenActivitySourceFrame = useCallback(
    (frameRecordId: string, nodeId?: string) => {
      const onOpenSourceFrame = props.signals.activity?.onOpenSourceFrame;
      if (!onOpenSourceFrame) return;
      openFrameInMobileGraph(frameRecordId, onOpenSourceFrame, () => setTab("graph"), nodeId);
    },
    [props.signals.activity?.onOpenSourceFrame]
  );
  const handleOpenReminderFrame = useCallback(
    (target: { frameRecordId: string; nodeId: string }) => {
      props.notifications.onOpenReminderFrame(target);
      setTab("graph");
    },
    [props.notifications]
  );
  const handleSelectInboxFrameRecord = useCallback(
    (frameRecordId: string) => {
      props.inbox.onSelectFrameRecord(frameRecordId);
      setTab("graph");
    },
    [props.inbox]
  );
  const [view, setView] = useState<GraphView>("graph");
  const [searchOpen, setSearchOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);
  const [readNotificationIds, setReadNotificationIds] = useState<Set<string>>(() => new Set());
  // The detail sheet is its own mobile state: it opens when the user taps a node,
  // NOT from the desktop's detailPanelOpen (which defaults open for the right pane).
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [exampleOpen, setExampleOpen] = useState(false);
  const agentInputRef = useRef<HTMLTextAreaElement>(null);

  // Keep the full-screen composer pinned above the on-screen keyboard: the visual
  // viewport shrinks when the keyboard opens, so we mirror that inset onto --mk-kb
  // (100dvh alone does not shrink under the iOS keyboard).
  useEffect(() => {
    const vv = window.visualViewport;
    if (!agentOpen || !vv) {
      return;
    }
    const apply = () => {
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty("--mk-kb", `${inset}px`);
    };
    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
      document.documentElement.style.removeProperty("--mk-kb");
    };
  }, [agentOpen]);

  // Auto-grow the composer with its content (single line → capped), like a chat input.
  useEffect(() => {
    const el = agentInputRef.current;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [props.message, agentOpen]);

  const closeAgent = () => {
    setAgentOpen(false);
    setExampleOpen(false);
  };

  const fillExample = () => {
    props.onUseSample();
    setExampleOpen(false);
    window.requestAnimationFrame(() => agentInputRef.current?.focus());
  };

  const nodes = frame.graph.nodes;
  const edges = frame.graph.edges;
  const stats = frame.ui.stats ?? { events: 0, opportunities: 0, organisations: 0, people: 0 };

  const nodeById = useMemo(() => {
    const map: Record<string, GraphNode> = {};
    nodes.forEach((n) => (map[n.id] = n));
    return map;
  }, [nodes]);

  const counts = useMemo(() => {
    const c: Record<GraphEntityType, number> = { person: 0, organisation: 0, event: 0, opportunity: 0, note: 0 };
    nodes.forEach((n) => (c[n.type] += 1));
    return c;
  }, [nodes]);

  const linkedForSelected = useMemo(() => {
    if (!selectedNode) return [] as { node: GraphNode; label: string }[];
    const out: { node: GraphNode; label: string }[] = [];
    edges.forEach((e) => {
      const otherId = e.source === selectedNode.id ? e.target : e.target === selectedNode.id ? e.source : null;
      if (otherId && nodeById[otherId]) {
        out.push({ node: nodeById[otherId], label: e.label });
      }
    });
    return out;
  }, [edges, nodeById, selectedNode]);

  const notificationItems = useMemo<MobileNotification[]>(() => {
    const reminders = props.notifications.relationshipReminders.map((reminder) => {
      const target = reminderFrameTarget(reminder);
      const sourceLabel = reminder.sourceNode?.label ?? reminder.edge.source;
      const targetLabel = reminder.targetNode?.label ?? reminder.edge.target;
      const tone = toneForEntityType(reminder.sourceNode?.entityType ?? reminder.targetNode?.entityType ?? "person");
      return {
        id: `reminder:${reminder.edge.id}`,
        body: t.mobile.relationshipReminderBody(sourceLabel, targetLabel, reminder.staleByDays),
        icon: reminder.staleByDays > 30 ? Flame : CalendarDays,
        onOpen: () => handleOpenReminderFrame(target),
        title: t.mobile.relationshipReminder,
        tone,
        when: compactDateTime(reminder.lastSeenAt)
      };
    });
    const signals = props.notifications.opportunitySignals.slice(0, 5).map((signal) => {
      const sourceFrameRecordId = opportunitySourceFrameRecordId(signal);
      return {
        id: `signal:${signal.id}`,
        body: signal.title,
        icon: CircleDollarSign,
        onOpen: () => {
          if (sourceFrameRecordId) {
            props.notifications.onOpenSignalFrame(sourceFrameRecordId);
            setTab("graph");
            return;
          }
          setTab("signals");
        },
        title: t.mobile.opportunitySignal,
        tone: "opportunity" as const,
        when: compactDateTime(signal.updatedAt)
      };
    });

    return [...reminders, ...signals];
  }, [props.notifications, t]);
  const unreadNotificationCount = notificationItems.filter((item) => !readNotificationIds.has(item.id)).length;
  const markNotificationRead = (id: string) => {
    setReadNotificationIds((current) => {
      const next = new Set(current);
      next.add(id);
      return next;
    });
  };
  const markAllNotificationsRead = () => {
    setReadNotificationIds(new Set(notificationItems.map((item) => item.id)));
  };
  const openNotification = (item: MobileNotification) => {
    markNotificationRead(item.id);
    item.onOpen();
    setNotificationsOpen(false);
  };

  const openNodeDetail = (nodeId: string) => {
    onSelectNode(nodeId);
    setDetailSheetOpen(true);
  };
  const closeDetail = () => {
    setDetailSheetOpen(false);
    onCloseDetail();
  };
  const handlePick = (nodeId: string) => {
    setTab("graph");
    setSearchOpen(false);
    openNodeDetail(nodeId);
  };
  const detailSheetVisible = detailSheetOpen && Boolean(selectedNode) && !agentOpen;

  return (
    <div className="mk-app">
      <header className="mk-header mk-header-safe">
        <div className="mk-brand">
          <span className="mk-brand-mark" aria-hidden="true">
            <Share2 size={20} color="var(--accent)" />
          </span>
          <span className="mk-title">{mobileTabTitle(tab, t)}</span>
        </div>
        <div className="mk-header-actions">
          <button
            className="mk-agent-trigger"
            aria-label={t.mobile.captureMeeting}
            onClick={() => setAgentOpen(true)}
            type="button"
          >
            <Sparkles size={16} aria-hidden="true" />
            <span>Agent</span>
          </button>
          <button className="mk-icon-btn" aria-label={t.workspace.searchAriaLabel} onClick={() => setSearchOpen(true)} type="button">
            <Search size={20} color="var(--text-secondary)" />
          </button>
          <button className="mk-icon-btn mk-bell" aria-label={t.mobile.notificationsTitle} onClick={() => setNotificationsOpen(true)} type="button">
            <Bell size={20} color="var(--text-secondary)" />
            {unreadNotificationCount > 0 ? <span className="mk-bell-dot">{unreadNotificationCount}</span> : null}
          </button>
        </div>
      </header>

      <main className="mk-main">
        {tab === "graph" && (
          <GraphTab
            frame={frame}
            view={view}
            onViewChange={setView}
            filters={filters}
            counts={counts}
            onToggleFilter={onToggleFilter}
            selectedNodeId={selectedNode?.id ?? null}
            onSelectNode={openNodeDetail}
          />
        )}
        {tab === "signals" && (
          <MobileSignalsTab
            signals={{
              ...props.signals,
              activity: props.signals.activity
                ? { ...props.signals.activity, onOpenSourceFrame: handleOpenActivitySourceFrame }
                : null,
              onOpenFrame: handleOpenSignalFrame
            }}
          />
        )}
        {tab === "inbox" && <InboxTab inbox={{ ...props.inbox, onSelectFrameRecord: handleSelectInboxFrameRecord }} />}
        {tab === "you" && <YouTab stats={stats} profile={props.profile} />}
      </main>

      <nav className="mk-dock" aria-label={t.mobile.primaryNavAriaLabel}>
        <TabButton id="graph" label={mobileTabTitle("graph", t)} active={tab} onSelect={setTab} icon={<Share2 size={22} />} />
        <TabButton id="signals" label={mobileTabTitle("signals", t)} active={tab} onSelect={setTab} icon={<TrendingUp size={22} />} />
        <TabButton id="inbox" label={mobileTabTitle("inbox", t)} active={tab} onSelect={setTab} icon={<InboxIcon size={22} />} />
        <TabButton id="you" label={mobileTabTitle("you", t)} active={tab} onSelect={setTab} icon={<UserRound size={22} />} />
      </nav>

      {/* Detail sheet — opens when the user taps a node (independent of the desktop right pane). */}
      <div className={detailSheetVisible ? "mk-scrim on" : "mk-scrim"} onClick={closeDetail} aria-hidden="true" />
      <div
        className={detailSheetVisible ? "mk-sheet on" : "mk-sheet"}
        role="dialog"
        aria-modal="true"
        aria-label={t.mobile.selectedEntity}
      >
        <span className="mk-sheet-grip" aria-hidden="true" />
        {selectedNode && (
          <DetailSheetBody
            node={selectedNode}
            linked={linkedForSelected}
            onClose={closeDetail}
            onSelect={openNodeDetail}
            isSelf={isSelfNode}
            onSetSelf={onSetSelfNode}
            onClearSelf={onClearSelfNode}
          />
        )}
      </div>

      {/* Agent capture — full-screen conversation */}
      <div className={agentOpen ? "mk-scrim on" : "mk-scrim"} onClick={closeAgent} aria-hidden="true" />
      <div
        className={agentOpen ? "mk-agent-full on" : "mk-agent-full"}
        role="dialog"
        aria-modal="true"
        aria-label={t.mobile.captureMeeting}
      >
        <header className="mk-agent-full-head">
          <span className="mk-agent-brand">
            <span className="mk-agent-brand-spark" aria-hidden="true">
              <Sparkles size={15} />
            </span>
            <span className="mk-sheet-eyebrow">{t.agentChat.graphChat}</span>
          </span>
          <div className="agent-mode-toggle" role="group" aria-label={t.agentChat.modeToggleAriaLabel}>
            <button
              type="button"
              className={props.agentMode === "graph-copilot" ? "agent-mode-toggle-option is-active" : "agent-mode-toggle-option"}
              aria-pressed={props.agentMode === "graph-copilot"}
              onClick={() => props.onAgentModeChange("graph-copilot")}
              title={t.agentChat.modeWriterHint}
            >
              <span>{t.agentChat.modeWriterLabel}</span>
            </button>
            <button
              type="button"
              className={props.agentMode === "seeker" ? "agent-mode-toggle-option is-active" : "agent-mode-toggle-option"}
              aria-pressed={props.agentMode === "seeker"}
              onClick={() => props.onAgentModeChange("seeker")}
              title={t.agentChat.modeSeekerHint}
            >
              <span>{t.agentChat.modeSeekerLabel}</span>
            </button>
          </div>
          <button className="mk-sheet-x" aria-label={t.mobile.close} onClick={closeAgent} type="button">
            <X size={18} color="var(--text-secondary)" />
          </button>
        </header>

        <div className="mk-agent-full-thread">
          <MobileAgentThread messages={props.chatMessages} busy={props.agentBusy} />
        </div>

        <div className="mk-agent-composer">
          {!props.message.trim() ? (
            <button className="mk-example-trigger" type="button" onClick={() => setExampleOpen(true)}>
              <Sparkles size={13} aria-hidden="true" />
              <span>{t.agentChat.seeExample}</span>
            </button>
          ) : null}
          <div className="mk-composer-bar">
            <textarea
              ref={agentInputRef}
              className="mk-composer-input"
              value={props.message}
              onChange={props.onMessageChange}
              placeholder={props.agentMode === "seeker" ? t.agentChat.seekerMessagePlaceholder : t.agentChat.messagePlaceholder}
              rows={1}
            />
            <button
              className="mk-composer-send"
              type="button"
              data-busy={props.agentBusy ? "true" : undefined}
              disabled={props.agentBusy || !props.message.trim()}
              aria-label={props.agentBusy ? t.mobile.generatingGraphFrame : t.mobile.generateGraphFrame}
              onClick={() => props.onSubmitAgent()}
            >
              {props.agentBusy ? (
                <Loader2 size={18} style={{ animation: "mk-spin .9s linear infinite" }} />
              ) : (
                <ArrowUp size={20} />
              )}
            </button>
          </div>
        </div>

        {exampleOpen ? (
          <div className="mk-example-pop-scrim" onClick={() => setExampleOpen(false)}>
            <div
              className="mk-example-pop"
              role="dialog"
              aria-label={t.agentChat.exampleOnly}
              onClick={(event) => event.stopPropagation()}
            >
              <div className="mk-example-pop-head">
                <span className="mk-sheet-eyebrow">{t.agentChat.exampleOnly}</span>
                <button className="mk-sheet-x" type="button" aria-label={t.mobile.close} onClick={() => setExampleOpen(false)}>
                  <X size={16} color="var(--text-secondary)" />
                </button>
              </div>
              <p className="mk-example-pop-text">{props.sampleText}</p>
              <Button variant="primary" size="md" fullWidth onClick={fillExample}>
                {t.agentChat.useExample}
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Search overlay */}
      <SearchOverlay open={searchOpen} nodes={nodes} onClose={() => setSearchOpen(false)} onPick={handlePick} />

      <NotifPanel
        error={props.notifications.error}
        items={notificationItems}
        onClose={() => setNotificationsOpen(false)}
        onMarkAllRead={markAllNotificationsRead}
        onOpenNotification={openNotification}
        open={notificationsOpen}
        pending={props.notifications.pending}
        readIds={readNotificationIds}
      />
    </div>
  );
}

function MobileAgentThread({ busy, messages }: { busy: boolean; messages: AgentChatMessage[] }) {
  const { t } = useI18n();
  const visibleMessages = messages.slice(-4);
  return (
    <div className="mk-agent-thread" role="log" aria-live="polite" aria-label={t.agentChat.agentConversationAriaLabel}>
      {visibleMessages.map((message) => {
        const assistant = message.role === "assistant";
        return (
          <article className={`mk-agent-msg ${assistant ? "assistant" : "user"}`} key={message.id}>
            <span className="mk-agent-msg-icon" aria-hidden="true">
              {assistant ? <Bot size={15} /> : <UserRound size={15} />}
            </span>
            <div className="mk-agent-msg-body">
              {mobileMessageParagraphs(message.content).map((paragraph, index) => (
                <p key={`${index}:${paragraph}`}>{paragraph}</p>
              ))}
              {/* Seeker turns are read-only discussion with no frame/commit — only the
                  graph-copilot extraction result has a receipt to show here. */}
              {message.result?.mode === "graph-copilot" ? (
                <dl className="mk-agent-receipt">
                  <div>
                    <dt>{t.agentChat.nodesLabel}</dt>
                    <dd>{message.result.frame.graph.nodes.length}</dd>
                  </div>
                  <div>
                    <dt>{t.agentChat.edgesLabel}</dt>
                    <dd>{message.result.frame.graph.edges.length}</dd>
                  </div>
                </dl>
              ) : null}
            </div>
          </article>
        );
      })}
      {busy ? (
        <div className="mk-agent-typing">
          <Loader2 size={14} style={{ animation: "mk-spin .9s linear infinite" }} />
          <span>{t.agentChat.structuringGraphFrame}</span>
        </div>
      ) : null}
    </div>
  );
}

function mobileMessageParagraphs(content: string) {
  return content
    .split(/ (?=Execution:|I still need)/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function mobileTabTitle(tab: MobileTab, t: Dictionary) {
  if (tab === "graph") {
    return t.mobile.graph;
  }
  if (tab === "signals") {
    return t.nav.signals;
  }
  if (tab === "inbox") {
    return t.mobile.inbox;
  }
  return t.mobile.you;
}

function TabButton({
  id,
  label,
  active,
  onSelect,
  icon
}: {
  id: MobileTab;
  label: string;
  active: MobileTab;
  onSelect: (tab: MobileTab) => void;
  icon: ReactNode;
}) {
  return (
    <button
      aria-current={active === id ? "page" : undefined}
      className={active === id ? "mk-dock-item is-active" : "mk-dock-item"}
      onClick={() => onSelect(id)}
      type="button"
    >
      <span className="mk-dock-icon" aria-hidden="true">{icon}</span>
      <span className={active === id ? "mk-dock-label" : "visually-hidden"}>{label}</span>
    </button>
  );
}

function GraphTab({
  frame,
  view,
  onViewChange,
  filters,
  counts,
  onToggleFilter,
  selectedNodeId,
  onSelectNode
}: {
  frame: GraphVisualizationFrame;
  view: GraphView;
  onViewChange: (view: GraphView) => void;
  filters: Record<GraphEntityType, boolean>;
  counts: Record<GraphEntityType, number>;
  onToggleFilter: (type: GraphEntityType) => void;
  selectedNodeId: string | null;
  onSelectNode: (nodeId: string) => void;
}) {
  const { t } = useI18n();
  const nodes = frame.graph.nodes;
  const events = useMemo(
    () =>
      nodes
        .filter((n) => n.type === "event")
        .sort((a, b) => (b.occurredAt ?? "").localeCompare(a.occurredAt ?? "")),
    [nodes]
  );
  const byType = (type: GraphEntityType) => nodes.filter((n) => n.type === type && filters[type] !== false);

  return (
    <>
      <div style={{ padding: "2px 16px 0" }}>
        <SegmentedControl
          value={view}
          onChange={(v) => onViewChange(v as GraphView)}
          options={[
            { label: t.mobile.graph, value: "graph" },
            { label: t.mobile.listView, value: "list" },
            { label: t.graphStage.timelineLabel, value: "timeline" }
          ]}
        />
      </div>

      <div className="mk-chips">
        {FILTER_ORDER.map((type) => {
          const on = filters[type] !== false;
          const meta = entityMeta[type];
          return (
            <button
              key={type}
              className="mk-chip"
              onClick={() => onToggleFilter(type)}
              type="button"
              aria-pressed={on}
              style={{
                background: on ? "var(--surface-card)" : "var(--surface-sunken)",
                color: on ? "var(--text-strong)" : "var(--text-muted)",
                borderColor: on ? meta.color : "var(--border-default)",
                opacity: on ? 1 : 0.6
              }}
            >
              <span className="mk-chip-dot" style={{ background: meta.color }} />
              {entityLabel(t, type)}
              <span className="mk-chip-ct">{counts[type]}</span>
            </button>
          );
        })}
      </div>

      {view === "graph" && (
        <div className="mk-body mk-body-graph">
          <GraphFlat frame={frame} filters={filters} selectedNodeId={selectedNodeId} onSelectNode={onSelectNode} />
        </div>
      )}

      {view === "list" && (
        <div className="mk-body">
          {FILTER_ORDER.map((type) => {
            const items = byType(type);
            if (items.length === 0) return null;
            return (
              <section className="mk-group" key={type}>
                <div className="mk-group-head">
                  <span>{entityLabel(t, type)}</span>
                  <span className="mk-group-ct">{items.length}</span>
                </div>
                <div className="mk-rows">
                  {items.map((n) => (
                    <EntityRow key={n.id} node={n} onClick={() => onSelectNode(n.id)} />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {view === "timeline" && (
        <div className="mk-body mk-timeline">
          {events.length === 0 ? (
            <p className="mk-graph-cap">{t.mobile.noDatedEvents}</p>
          ) : (
            events.map((n) => (
              <div className="mk-tl-item" key={n.id}>
                <span className="mk-tl-dot" />
                <span className="mk-tl-date">{n.occurredAt ? n.occurredAt.slice(0, 10) : t.mobile.undated}</span>
                <div className="mk-rows">
                  <EntityRow node={n} onClick={() => onSelectNode(n.id)} />
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </>
  );
}

function EntityRow({ node, onClick, trailing }: { node: GraphNode; onClick: () => void; trailing?: ReactNode }) {
  const { t } = useI18n();
  const meta = entityMeta[node.type] ?? entityMeta.note;
  const Icon = meta.icon;
  return (
    <ListRow
      leading={
        <span
          className="mk-row-avatar"
          style={{ background: `color-mix(in srgb, ${meta.color} 16%, transparent)`, color: meta.color }}
          aria-hidden="true"
        >
          <Icon size={18} />
        </span>
      }
      onClick={onClick}
      subtitle={nodeSubtitle(node, t)}
      title={node.label}
      trailing={trailing}
    />
  );
}

function DetailSheetBody({
  node,
  linked,
  onClose,
  onSelect,
  isSelf,
  onSetSelf,
  onClearSelf
}: {
  node: GraphNode;
  linked: { node: GraphNode; label: string }[];
  onClose: () => void;
  onSelect: (nodeId: string) => void;
  isSelf: boolean;
  onSetSelf: () => void;
  onClearSelf: () => void;
}) {
  const { t } = useI18n();
  const meta = entityMeta[node.type] ?? entityMeta.note;
  const Icon = meta.icon;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div className="mk-sheet-head">
        <span
          className="mk-row-avatar"
          style={{ background: `color-mix(in srgb, ${meta.color} 16%, transparent)`, color: meta.color, width: 48, height: 48 }}
          aria-hidden="true"
        >
          <Icon size={24} />
        </span>
        <div className="mk-sheet-titles">
          <div className="mk-sheet-eyebrow" style={{ color: meta.color }}>
            {entitySingularLabel(t, node.type)}
          </div>
          <div className="mk-sheet-name">{node.label}</div>
        </div>
        <button className="mk-sheet-x" aria-label={t.mobile.close} onClick={onClose} type="button">
          <X size={18} color="var(--text-secondary)" />
        </button>
      </div>

      <button
        aria-pressed={isSelf}
        className={isSelf ? "self-node-toggle is-self" : "self-node-toggle"}
        onClick={isSelf ? onClearSelf : onSetSelf}
        style={{ alignSelf: "flex-start" }}
        type="button"
      >
        <UserRound size={13} />
        {isSelf ? t.detail.selfYou : t.detail.selfSet}
      </button>

      {node.summary && <p className="mk-sheet-about">{node.summary}</p>}

      {linked.length > 0 && (
        <div className="mk-linked">
          <div className="mk-eyebrow">{t.mobile.linkedEntitiesCount(linked.length)}</div>
          <div className="mk-rows">
            {linked.map((l, i) => (
              <EntityRow
                key={`${l.node.id}-${i}`}
                node={l.node}
                onClick={() => onSelect(l.node.id)}
                trailing={<span className="mk-rel">{l.label}</span>}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function MobileSignalsTab({ signals }: { signals: MobileWorkspaceProps["signals"] }) {
  const { t } = useI18n();
  const [surface, setSurface] = useState<"activity" | "opportunity">("activity");
  const activeSurface = signals.activity ? surface : "opportunity";
  const activityTabId = useId();
  const activityPanelId = useId();
  const opportunityTabId = useId();
  const opportunityPanelId = useId();
  const activityTabRef = useRef<HTMLButtonElement>(null);
  const opportunityTabRef = useRef<HTMLButtonElement>(null);
  const dispositionCounts = opportunityInboxCounts(signals.items);

  const selectSurface = (nextSurface: "activity" | "opportunity", moveFocus = false) => {
    setSurface(nextSurface);
    if (moveFocus) {
      (nextSurface === "activity" ? activityTabRef : opportunityTabRef).current?.focus();
    }
  };
  const handleSurfaceKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    let nextSurface: "activity" | "opportunity" | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      nextSurface = activeSurface === "activity" ? "opportunity" : "activity";
    } else if (event.key === "Home") {
      nextSurface = "activity";
    } else if (event.key === "End") {
      nextSurface = "opportunity";
    }
    if (nextSurface) {
      event.preventDefault();
      selectSurface(nextSurface, true);
    }
  };

  return (
    <div className="mk-body">
      {signals.activity ? (
        <div className="mk-surface-tabs" role="tablist" aria-label={t.activityFeed.tabsAriaLabel}>
          <button
            aria-controls={activityPanelId}
            aria-selected={activeSurface === "activity"}
            className={activeSurface === "activity" ? "mk-surface-tab is-active" : "mk-surface-tab"}
            id={activityTabId}
            onClick={() => selectSurface("activity")}
            onKeyDown={handleSurfaceKeyDown}
            ref={activityTabRef}
            role="tab"
            tabIndex={activeSurface === "activity" ? 0 : -1}
            type="button"
          >
            <span>{t.activityFeed.activityTab}</span>
            <span className="mk-group-ct">{signals.activity.items.length}</span>
          </button>
          <button
            aria-controls={opportunityPanelId}
            aria-selected={activeSurface === "opportunity"}
            className={activeSurface === "opportunity" ? "mk-surface-tab is-active" : "mk-surface-tab"}
            id={opportunityTabId}
            onClick={() => selectSurface("opportunity")}
            onKeyDown={handleSurfaceKeyDown}
            ref={opportunityTabRef}
            role="tab"
            tabIndex={activeSurface === "opportunity" ? 0 : -1}
            type="button"
          >
            <span>{t.activityFeed.inboxTab}</span>
            <span className="mk-group-ct">{signals.items.length}</span>
          </button>
        </div>
      ) : null}

      {activeSurface === "activity" && signals.activity ? (
        <section
          aria-labelledby={activityTabId}
          id={activityPanelId}
          role="tabpanel"
          tabIndex={0}
        >
          <ActivityFeedPanel {...signals.activity} />
        </section>
      ) : (
        <div
          aria-labelledby={signals.activity ? opportunityTabId : undefined}
          id={signals.activity ? opportunityPanelId : undefined}
          role={signals.activity ? "tabpanel" : undefined}
          tabIndex={signals.activity ? 0 : undefined}
        >
          <div className="mk-hero-card">
            <span className="mk-eyebrow">{t.mobile.thisWeek}</span>
            <p className="mk-hero-line">{t.mobile.opportunityHero}</p>
            <div className="mk-hero-stat">
              {t.signals.inboxSummary(dispositionCounts.pending, dispositionCounts.saved)}
            </div>
          </div>
          <section className="mk-group">
            <div className="mk-group-head">
              <span>{t.mobile.opportunityFeed}</span>
              <span className="mk-group-ct">{signals.items.length}</span>
            </div>
            <div className="mk-rows">
              {!signals.enabled ? (
                <EmptyState icon={<TrendingUp size={24} />} title={t.signals.signInToLoad}>
                  {t.mobile.signInSignalsBody}
                </EmptyState>
              ) : signals.pending ? (
                <Spinner label={t.signals.loading} style={{ justifyContent: "center", padding: "24px 0" }} />
              ) : signals.error ? (
                <p className="mk-graph-cap" role="alert">
                  {signals.error}
                </p>
              ) : signals.items.length === 0 ? (
                <EmptyState icon={<TrendingUp size={24} />} title={t.signals.empty}>
                  {t.mobile.emptySignalsBody}
                </EmptyState>
              ) : (
                signals.items.map((item) => <SignalFeedItem item={item} key={item.id} signals={signals} />)
              )}
            </div>
          </section>
          {signals.actionError ? (
            <p className="mk-graph-cap" role="alert">
              {signals.actionError}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

function SignalFeedItem({ item, signals }: { item: GraphOpportunityFeedItem; signals: MobileWorkspaceProps["signals"] }) {
  const { t } = useI18n();
  const score = Math.round(item.score * 100);
  const actionPending = signals.actionPendingId === item.id;
  const sourceFrameRecordId = opportunitySourceFrameRecordId(item);
  const reason = opportunityReason(item) ?? opportunityInsightType(item);
  const followUpRequestedAt = opportunityFollowUpRequestedAt(item);
  const hasEvidence = opportunityHasEvidence(item);
  const actionDisabled = signals.readOnly || item.status === "dismissed" || actionPending;

  return (
    <article className="mk-signal-card">
      <div className="mk-signal-top">
        <Badge tone="opportunity" size="sm">
          {opportunityStatusLabel(item.status, t)}
        </Badge>
        <span aria-label={t.signals.confidence(score)} title={t.signals.confidence(score)}>
          <ConfidenceMeter value={score} segments={4} size="sm" />
        </span>
      </div>
      <h3 className="mk-signal-title">{item.title}</h3>
      <p className="mk-signal-summary">{item.summary}</p>
      <div className="mk-signal-meta">
        <span>{t.mobile.updatedAt(compactDateTime(item.updatedAt))}</span>
        <span>{t.signals.evidence}: {hasEvidence ? t.signals.evidenceAvailable : t.signals.evidenceUnavailable}</span>
        <span>{t.signals.source}: {sourceFrameRecordId ? t.signals.sourceAvailable : t.signals.sourceUnavailable}</span>
      </div>
      {reason ? <p className="mk-signal-reason">{reason}</p> : null}
      {followUpRequestedAt ? (
        <p className="mk-signal-reason">{t.signals.followUpRequested(compactDateTime(followUpRequestedAt))}</p>
      ) : null}
      <div className="mk-signal-actions" aria-label={t.signals.actionsFor(item.title)}>
        {sourceFrameRecordId ? (
          <Button
            iconLeft={<ExternalLink size={14} />}
            onClick={() => signals.onOpenFrame(sourceFrameRecordId)}
            size="sm"
            variant="secondary"
          >
            {t.mobile.frame}
          </Button>
        ) : null}
        {signalActions.map((action) => {
          const Icon = actionPending ? Loader2 : action.icon;
          const label = opportunityActionLabel(action.action, item, t);
          return (
            <Button
              disabled={actionDisabled || opportunityActionAlreadyApplied(action.action, item)}
              iconLeft={<Icon size={14} style={actionPending ? { animation: "mk-spin .9s linear infinite" } : undefined} />}
              key={action.action}
              onClick={() => signals.onAction(item.id, action.action)}
              size="sm"
              variant={action.action === "follow_up" ? "subtle" : "secondary"}
            >
              {label}
            </Button>
          );
        })}
      </div>
    </article>
  );
}

function InboxTab({ inbox }: { inbox: MobileWorkspaceProps["inbox"] }) {
  const { t } = useI18n();
  return (
    <div className="mk-body">
      <div className="mk-inbox-note">
        <Sparkles size={15} color="var(--accent)" />
        {t.mobile.notesCaptured}
      </div>
      <section className="mk-group">
        <div className="mk-group-head">
          <span>{t.mobile.committedFrames}</span>
          <span className="mk-group-ct">{inbox.items.length}</span>
        </div>
        <div className="mk-rows">
          {inbox.pending ? (
            <Spinner label={t.leftRail.loadingFrames} style={{ justifyContent: "center", padding: "24px 0" }} />
          ) : inbox.error ? (
            <p className="mk-graph-cap" role="alert">
              {t.leftRail.frameHistoryUnavailable}
            </p>
          ) : inbox.items.length === 0 ? (
            <EmptyState icon={<InboxIcon size={24} />} title={t.mobile.noCommittedFrames}>
              {t.leftRail.noFramesYet}
            </EmptyState>
          ) : (
            inbox.items.map((item) => {
              const active = item.id === inbox.activeFrameRecordId;
              return (
                <ListRow
                  key={item.id}
                  leading={
                    <span className="mk-row-avatar" style={{ background: "var(--accent-soft)", color: "var(--accent)" }} aria-hidden="true">
                      <GitBranch size={18} />
                    </span>
	                  }
	                  onClick={() => inbox.onSelectFrameRecord(item.id)}
	                  subtitle={t.mobile.frameNodeSummary(item.nodeCount, compactDateTime(item.acceptedAt))}
	                  title={frameHistoryTitle(item)}
	                  trailing={
	                    active ? (
	                      <Badge tone="opportunity" size="sm">
	                        {t.mobile.current}
	                      </Badge>
                    ) : (
                      <ExternalLink size={16} color="var(--text-muted)" />
                    )
                  }
                  {...(active ? { style: { borderColor: "var(--accent)", background: "var(--accent-soft)" } } : {})}
                />
              );
            })
          )}
        </div>
      </section>
      {inbox.restoreErrorFrameId ? (
        <p className="mk-graph-cap" role="alert">
          {t.leftRail.frameLoadError(inbox.restoreErrorFrameId)}
        </p>
      ) : null}
    </div>
  );
}

function YouTab({
  stats,
  profile
}: {
  stats: { events: number; opportunities: number; organisations: number; people: number };
  profile: MobileWorkspaceProps["profile"];
}) {
  const { t } = useI18n();
  const { agentAuthLabel, availableAuthProviders, install, onOpenBilling, onOpenSettings, onSignIn, onSignOut, storedSession } =
    profile;
  const displayName = storedSession?.name ?? storedSession?.email ?? agentAuthLabel;
  const displaySub = storedSession
    ? [storedSession.email, `tenant ${shortScopeId(storedSession.tenantId)}`].filter(Boolean).join(" · ")
    : t.mobile.signInToSync;
  const profileInitials = initialsFor(displayName);
  return (
    <div className="mk-body">
      <div className="mk-profile">
        <Avatar initials={profileInitials} tone="person" size="xl" glow />
        <div className="mk-profile-name">{displayName}</div>
        <div className="mk-profile-sub">{displaySub}</div>
      </div>

      <AccountSurface
        availableAuthProviders={availableAuthProviders}
        onOpenBilling={onOpenBilling}
        onOpenSettings={onOpenSettings}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
        session={storedSession}
        variant="mobile"
      />

      <div className="mk-stat-grid">
        <div className="mk-stat3">
          <span className="mk-stat3-v" style={{ color: "var(--entity-person)" }}>
            {stats.people}
          </span>
          <span className="mk-stat3-l">{entityLabel(t, "person")}</span>
        </div>
        <div className="mk-stat3">
          <span className="mk-stat3-v" style={{ color: "var(--entity-company)" }}>
            {stats.organisations}
          </span>
          <span className="mk-stat3-l">{entityLabel(t, "organisation")}</span>
        </div>
        <div className="mk-stat3">
          <span className="mk-stat3-v" style={{ color: "var(--entity-event)" }}>
            {stats.events}
          </span>
          <span className="mk-stat3-l">{entityLabel(t, "event")}</span>
        </div>
      </div>

      {install && (
        <div className={install.installed ? "mk-install done" : "mk-install"}>
          <div className="mk-install-mark">
            {install.installed ? <Check size={20} color="#fff" /> : <Share2 size={20} color="#fff" />}
          </div>
          <div className="mk-install-body">
            <div className="mk-install-title">{install.installed ? t.mobile.installedOnThisDevice : t.mobile.installSalesko}</div>
            <div className="mk-install-sub">
              {install.installed
                ? t.mobile.installedPwaDescription
                : install.canPrompt
                  ? t.mobile.installReadyDescription
                  : t.mobile.installFallbackDescription}
            </div>
          </div>
          {!install.installed && install.canPrompt && (
            <Button variant="primary" size="sm" onClick={install.prompt}>
              {t.mobile.install}
            </Button>
          )}
        </div>
      )}

      <div className="mk-rows">
        <div className="mk-lang-row">
          <Globe size={18} color="var(--text-secondary)" />
          <span className="mk-set-t">{t.common.language}</span>
          <LanguageSwitcher className="mk-lang-switch" />
        </div>
        <SettingRow
          icon={<Plug size={18} color="var(--text-secondary)" />}
          label={t.mobile.mcpAccess}
          value={storedSession ? t.mobile.proPlanRequired : t.mobile.signInRequired}
        />
        <SettingRow
          icon={<Bell size={18} color="var(--text-secondary)" />}
          label={t.mobile.signalsAndReminders}
          value={storedSession ? t.mobile.tenantFeedActive : t.signals.unavailable}
        />
        <SettingRow
          icon={<Shield size={18} color="var(--text-secondary)" />}
          label={t.mobile.privacy}
          value={storedSession ? t.accountSurface.tenantLabel(shortScopeId(storedSession.tenantId)) : t.mobile.localPreview}
        />
      </div>

      <div className="mk-pwa-foot">
        <WifiOff size={13} color="var(--text-muted)" /> {t.mobile.graphCachedOffline}
      </div>
    </div>
  );
}

function SettingRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="mk-set-row">
      {icon}
      <span className="mk-set-t">{label}</span>
      <span className="mk-set-v">{value}</span>
    </div>
  );
}

function NotifPanel({
  error,
  items,
  onClose,
  onMarkAllRead,
  onOpenNotification,
  open,
  pending,
  readIds
}: {
  error: string | undefined;
  items: MobileNotification[];
  onClose: () => void;
  onMarkAllRead: () => void;
  onOpenNotification: (item: MobileNotification) => void;
  open: boolean;
  pending: boolean;
  readIds: Set<string>;
}) {
  const { t } = useI18n();
  return (
    <Sheet open={open} side="top" container="inline" title={t.mobile.notificationsTitle} onClose={onClose}>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button className="mk-markall" disabled={items.length === 0} onClick={onMarkAllRead} type="button">
          {t.mobile.markAllRead}
        </button>
        {pending ? <Spinner label={t.mobile.refreshingSignals} size={20} /> : null}
        {error ? (
          <p className="mk-graph-cap" role="alert">
            {error}
          </p>
        ) : null}
        {items.length === 0 && !pending ? (
          <EmptyState icon={<Bell size={24} />} title={t.mobile.noNotificationsTitle}>
            {t.mobile.notificationsEmpty}
          </EmptyState>
        ) : (
          items.map((item) => {
            const Icon = item.icon;
            const unread = !readIds.has(item.id);
            return (
              <button
                className={unread ? "mk-notif unread" : "mk-notif"}
                key={item.id}
                onClick={() => onOpenNotification(item)}
                type="button"
              >
                <span className="mk-notif-ic" style={notificationIconStyle(item.tone)} aria-hidden="true">
                  <Icon size={18} color={toneColor(item.tone)} />
                </span>
                <span className="mk-notif-body">
                  <span className="mk-notif-top">
                    <span className="mk-notif-title">{item.title}</span>
                    <span className="mk-notif-when">{item.when}</span>
                  </span>
                  <span className="mk-notif-text">{item.body}</span>
                </span>
                {unread ? <span className="mk-notif-dot" /> : null}
              </button>
            );
          })
        )}
      </div>
    </Sheet>
  );
}

function SearchOverlay({
  open,
  nodes,
  onClose,
  onPick
}: {
  open: boolean;
  nodes: GraphNode[];
  onClose: () => void;
  onPick: (nodeId: string) => void;
}) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const ql = q.trim().toLowerCase();
  const results = nodes.filter(
    (n) => !ql || n.label.toLowerCase().includes(ql) || (n.summary ?? "").toLowerCase().includes(ql)
  );
  const byType = (type: GraphEntityType) => results.filter((n) => n.type === type);

  return (
    <div className={open ? "mk-overlay on" : "mk-overlay"} role="dialog" aria-hidden={!open} aria-label={t.workspace.searchAriaLabel}>
      <div className="mk-search-bar mk-search-safe">
        <label className="mk-search-input">
          <Search size={19} color="var(--text-muted)" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t.workspace.searchPlaceholder}
            aria-label={t.workspace.searchAriaLabel}
          />
          {q && (
            <button className="mk-search-clear" onClick={() => setQ("")} aria-label={t.mobile.clearSearch} type="button">
              <X size={16} color="var(--text-muted)" />
            </button>
          )}
        </label>
        <button className="mk-search-cancel" onClick={onClose} type="button">
          {t.common.cancel}
        </button>
      </div>
      <div className="mk-search-results">
        {results.length === 0 ? (
          <div className="mk-search-empty">
            <Search size={26} color="var(--text-muted)" />
            <span>{t.mobile.noMatchesFor(q)}</span>
          </div>
        ) : (
          (["person", "organisation", "event", "opportunity"] as GraphEntityType[]).map((type) =>
            byType(type).length === 0 ? null : (
              <section className="mk-group" key={type}>
                <div className="mk-group-head">
                  <span>{entityLabel(t, type)}</span>
                  <span className="mk-group-ct">{byType(type).length}</span>
                </div>
                <div className="mk-rows">
                  {byType(type).map((n) => (
                    <EntityRow key={n.id} node={n} onClick={() => onPick(n.id)} />
                  ))}
                </div>
              </section>
            )
          )
        )}
      </div>
    </div>
  );
}

function reminderFrameTarget(reminder: GraphRelationshipReminder) {
  const nodeId = reminder.targetNode?.id ?? reminder.sourceNode?.id ?? reminder.edge.target ?? reminder.edge.source;
  return {
    frameRecordId: reminder.edge.frameRecordId,
    nodeId
  };
}

function frameHistoryTitle(item: GraphFrameListItem) {
  const summary = stableGraphFrameSummaryTitle(item.summary);
  if (summary.length === 0) {
    return item.id;
  }

  return summary.length > 64 ? `${summary.slice(0, 61)}…` : summary;
}

function toneForEntityType(type: GraphEntityType): EntityTone {
  if (type === "organisation") {
    return "company";
  }
  return type;
}

function toneColor(tone: BadgeTone) {
  if (tone === "neutral") {
    return "var(--warm-400)";
  }
  if (tone === "company") {
    return "var(--entity-company)";
  }
  return `var(--entity-${tone})`;
}

function notificationIconStyle(tone: BadgeTone) {
  const color = toneColor(tone);
  return {
    background: `color-mix(in srgb, ${color} 16%, transparent)`,
    color
  };
}

function initialsFor(label: string) {
  const initials = label
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return initials || "SK";
}

function shortScopeId(id: string) {
  return id.length <= 10 ? id : `${id.slice(0, 6)}...${id.slice(-3)}`;
}
